import { useEffect, useState, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import api from '../../../api/client'
import { Icon } from '../../../../public/icons/Icon.tsx'

interface FileItem {
	id: string
	filename: string
	original_filename?: string
	content_type?: string
	size_bytes: number
	url: string
	created_at?: string
	presigned_url?: string
	/** WebP-превью (≤400px) для лёгких миниатюр; выбор/использование — оригинал. */
	preview_url?: string | null
}

interface FilesResponse {
	files: FileItem[]
	total: number
}

type Tab = 'images' | 'videos' | 'audios'

interface TabState {
	files: FileItem[]
	total: number
	loading: boolean
	loadingMore: boolean
	loaded: boolean
}

const emptyTab = (): TabState => ({ files: [], total: 0, loading: false, loadingMore: false, loaded: false })

interface LazyItemProps {
	file: FileItem
	tab: Tab
	onVisible: (id: string) => void
	onSelect: (url: string, previewUrl?: string) => void
}

function LazyItem({ file, tab, onVisible, onSelect }: LazyItemProps) {
	const ref = useRef<HTMLButtonElement>(null)

	// Лениво тянем ОРИГИНАЛ только если нет webp-превью (старые файлы). Для файлов
	// с preview_url показываем превью сразу, оригинал берём из url.
	useEffect(() => {
		if (file.preview_url || file.presigned_url || !ref.current) return
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					onVisible(file.id)
					observer.disconnect()
				}
			},
			{ rootMargin: '100px' },
		)
		observer.observe(ref.current)
		return () => observer.disconnect()
	}, [file.id, file.preview_url, file.presigned_url, onVisible])

	// Миниатюра — лёгкое webp-превью; выбор/использование — ОРИГИНАЛ в полном
	// размере (url всегда есть из списка, presigned_url — если уже подгружен).
	const thumb = file.preview_url || file.presigned_url
	const full = file.presigned_url || file.url

	return (
		<button
			ref={ref}
			// У аудио визуального превью не бывает — «скелет-ожидание» не вешаем,
			// тайл кликабелен сразу (url приходит списком, presigned догрузится).
			className={`gallery-picker__item${!thumb && tab !== 'audios' ? ' gallery-picker__item--loading' : ''}`}
			type="button"
			onClick={() => full && onSelect(full, file.preview_url || undefined)}
			disabled={!full}
			title={file.original_filename || file.filename}
		>
			{tab === 'audios' ? (
				<span className="gallery-picker__audio" aria-hidden="true">
					<Icon id="gen-audio" size={22} />
					<span className="gallery-picker__audio-name">
						{(file.original_filename || file.filename).slice(0, 18)}
					</span>
				</span>
			) : thumb ? (
				tab === 'images' ? (
					<img
						src={thumb}
						alt={file.original_filename || file.filename}
						loading="lazy"
						onError={e => { const img = e.currentTarget; if (full && img.src !== full) img.src = full }}
					/>
				) : (
					<video
						src={full}
						poster={file.preview_url || undefined}
						muted
						playsInline
						preload={file.preview_url ? 'none' : 'metadata'}
						onMouseEnter={e => e.currentTarget.play()}
						onMouseLeave={e => { e.currentTarget.pause(); e.currentTarget.currentTime = 0 }}
					/>
				)
			) : (
				<div className="gallery-picker__item-skeleton" />
			)}
		</button>
	)
}

interface Props {
	initialTab?: Tab
	onSelect: (url: string, previewUrl?: string) => void
	onClose: () => void
}

export default function GalleryPickerModal({ initialTab = 'images', onSelect, onClose }: Props) {
	const { t } = useTranslation()
	const tab: Tab = initialTab
	const [tabs, setTabs] = useState<Record<Tab, TabState>>({ images: emptyTab(), videos: emptyTab(), audios: emptyTab() })

	const pendingRequests = useRef<Set<string>>(new Set())
	const semaphoreRef = useRef({ active: 0, queue: [] as Array<() => void> })
	const mountedRef = useRef(true)
	const tabsRef = useRef(tabs)
	useEffect(() => {
		mountedRef.current = true
		return () => { mountedRef.current = false }
	}, [])

	useEffect(() => { tabsRef.current = tabs }, [tabs])

	useEffect(() => {
		const prev = document.body.style.overflow
		document.body.style.overflow = 'hidden'
		return () => { document.body.style.overflow = prev }
	}, [])

	useEffect(() => {
		const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
		document.addEventListener('keydown', handler)
		return () => document.removeEventListener('keydown', handler)
	}, [onClose])

	const handleVisible = useCallback((id: string, t: Tab) => {
		if (pendingRequests.current.has(id)) return
		const file = tabsRef.current[t].files.find(f => f.id === id)
		if (!file || file.presigned_url) return
		pendingRequests.current.add(id)

		const sem = semaphoreRef.current
		const run = () => {
			api.request<{ url: string }>(`/api/v1/files/url/${id}`)
				.then(({ url }) => {
					if (!mountedRef.current) return
					setTabs(p => ({
						...p,
						[t]: { ...p[t], files: p[t].files.map(f => f.id === id ? { ...f, presigned_url: url } : f) },
					}))
				})
				.catch(() => {})
				.finally(() => {
					pendingRequests.current.delete(id)
					sem.active--
					const next = sem.queue.shift()
					if (next) next()
				})
		}

		if (sem.active < 5) {
			sem.active++
			run()
		} else {
			sem.queue.push(() => { sem.active++; run() })
		}
	}, [])

	const loadTab = useCallback(async (t: Tab, append = false) => {
		const cat = t === 'images' ? 'images' : t === 'audios' ? 'audio' : 'videos'
		const offset = append ? tabsRef.current[t].files.length : 0
		setTabs(prev => ({ ...prev, [t]: { ...prev[t], ...(append ? { loadingMore: true } : { loading: true }) } }))
		try {
			// Серверная фильтрация по category — иначе вкладка пуста, пока не догрузишь
			// всю смешанную ленту. preview_url приходит в этом же ответе.
			const response = await api.request<FilesResponse>(`/api/v1/files?limit=50&offset=${offset}&category=${cat}`)
			const filtered = response.files
			if (!mountedRef.current) return
			setTabs(prev => ({
				...prev,
				[t]: {
					...prev[t],
					files: append ? [...prev[t].files, ...filtered] : filtered,
					total: response.total,
					loaded: true,
					loading: false,
					loadingMore: false,
				},
			}))
		} catch {
			if (!mountedRef.current) return
			setTabs(prev => ({ ...prev, [t]: { ...prev[t], loading: false, loadingMore: false } }))
		}
	}, [])

	useEffect(() => {
		if (tabs[tab].loaded || tabs[tab].loading) return
		// setTimeout(0): loadTab синхронно ставит loading=true — прямой вызов
		// в теле эффекта ловится правилом set-state-in-effect (каскадный
		// ре-рендер). Отложенный тик разрывает синхронную цепочку.
		const id = window.setTimeout(() => loadTab(tab), 0)
		return () => window.clearTimeout(id)
	}, [tab, tabs, loadTab])

	const current = tabs[tab]

	return createPortal(
		<div className="gallery-picker-backdrop" onClick={onClose}>
			<div className="gallery-picker" onClick={e => e.stopPropagation()}>

				<div className="gallery-picker__header">
					<span className="gallery-picker__title">
						{tab === 'audios'
							? t('generate.gallery.selectAudio')
							: tab === 'videos'
								? t('generate.gallery.selectVideo')
								: t('generate.gallery.selectPhoto')}
					</span>
					<button className="gallery-picker__close" onClick={onClose} type="button" aria-label={t('common.close')}>
						<Icon id="gen-close" size={14} />
					</button>
				</div>

				<div className="gallery-picker__body">
					{current.loading ? (
						<div className="gallery-picker__grid">
							{Array.from({ length: 18 }).map((_, i) => (
								<div key={i} className="gallery-picker__skeleton" />
							))}
						</div>
					) : current.files.length === 0 ? (
						<p className="gallery-picker__empty">
							{tab === 'audios'
								? t('generate.gallery.noAudio')
								: tab === 'images'
									? t('generate.gallery.noPhotos')
									: t('generate.gallery.noVideos')}
						</p>
					) : (
						<div className="gallery-picker__grid">
							{current.files.map(file => (
								<LazyItem
									key={file.id}
									file={file}
									tab={tab}
									onVisible={id => handleVisible(id, tab)}
									onSelect={onSelect}
								/>
							))}
						</div>
					)}
				</div>

				{current.loaded && current.total > 0 && (
					<div className="gallery-picker__footer">
						{current.files.length < current.total ? (
							<button
								className="gallery-picker__load-more"
								type="button"
								onClick={() => loadTab(tab, true)}
								disabled={current.loadingMore}
							>
								{current.loadingMore ? t('common.loading') : t('generate.gallery.loadMore')}
							</button>
						) : (
							<div />
						)}
						<span className="gallery-picker__footer-count">
							{current.files.length} из {current.total}
						</span>
					</div>
				)}

			</div>
		</div>,
		document.body,
	)
}
